/**
 * Services API - for consultant/consultation booking
 */

import { API_ENDPOINTS, BUSINESS_ID, resolveShopBusinessId } from '../config/api';

/**
 * Resolve which business's conversation services/bookings to use for this request.
 * Prefers an explicitly-passed businessId, otherwise resolves per-tenant the same way
 * contentService does (REACT_APP_BUSINESS_ID env var, falling back to a domain lookup)
 * instead of assuming Rainbow Sails' hardcoded id — this is what makes "Book a
 * Conversation" work for any business's storefront, not just this one's build.
 */
async function resolveBusinessId(explicit) {
  if (explicit) return explicit;
  const resolved = await resolveShopBusinessId();
  return resolved || BUSINESS_ID;
}

export async function getServices(params = {}) {
  const businessId = await resolveBusinessId(params.businessId);
  const query = { businessId, serviceType: 'bookable_individual', ...params };
  const res = await fetch(API_ENDPOINTS.services.public(query));
  if (!res.ok) throw new Error('Failed to load services');
  const data = await res.json();
  return data.services || [];
}

export async function getService(id) {
  const res = await fetch(API_ENDPOINTS.services.byId(id));
  if (!res.ok) throw new Error('Service not found');
  const data = await res.json();
  return data.service;
}

export async function getAvailableSlots(serviceId, date, businessId) {
  const resolvedBusinessId = await resolveBusinessId(businessId);
  const params = { serviceId, date };
  if (resolvedBusinessId) params.businessId = resolvedBusinessId;
  const res = await fetch(API_ENDPOINTS.bookings.availableSlots(params));
  if (!res.ok) throw new Error('Failed to load available slots');
  const data = await res.json();
  return data.slots || [];
}

/** Calendar-aware slots (requires Authorization when filtering by Mango personal calendar). */
export async function fetchAvailableSlotsForMe(serviceId, date, businessId, authHeaders = {}) {
  const resolvedBusinessId = await resolveBusinessId(businessId);
  const params = { serviceId, date };
  if (resolvedBusinessId) params.businessId = resolvedBusinessId;
  const res = await fetch(API_ENDPOINTS.bookings.availableSlotsForMe(params), {
    headers: { ...authHeaders },
  });
  if (!res.ok) throw new Error('Failed to load available slots');
  return res.json();
}

export async function fetchCalendarPreview(startIso, endIso, authHeaders = {}) {
  const res = await fetch(
    API_ENDPOINTS.bookings.calendarPreview({ startDate: startIso, endDate: endIso }),
    { headers: { ...authHeaders } }
  );
  if (!res.ok) throw new Error('Failed to load calendar preview');
  return res.json();
}

export async function createPublicBooking({
  serviceId,
  startTime,
  customerName,
  customerEmail,
  customerPhone,
  deliveryMethod,
  notes,
  _hp,
  authHeaders = {},
}) {
  const res = await fetch(API_ENDPOINTS.bookings.public, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      serviceId,
      startTime,
      customerName,
      customerEmail,
      customerPhone: customerPhone || undefined,
      deliveryMethod,
      notes: notes || undefined,
      _hp: _hp || undefined,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Booking failed');
  return data;
}
